// "A tale of two clocks": a coarse JS interval only *drives* scheduling;
// every note is placed on the Web Audio clock itself with exact `when`
// timestamps, a lookahead window ahead of playback. The old system played
// each note the moment a setTimeout fired, which is why it drifted and
// jittered under load — timers slip, ctx.currentTime doesn't.
const DRIVER_INTERVAL_MS = 25;
const LOOKAHEAD_SEC = 0.12;
const CROSSFADE_SEC = 0.7;
const INTENSITY_RAMP_SEC = 0.4;
const STEPS = 16; // one loop = 16 8th-note steps (2 bars of 4/4) — see data/musicThemes.js

// Internal mix levels per layer, relative to the master gain (which carries
// the user's music-volume setting). Kept here, not in theme data — they're
// synthesis-engine balance, not composition.
const NOTE_LEVEL = { bass: 0.22, melody: 0.12, pad: 0.05 };
const KICK_LEVEL = 0.5;
const HAT_LEVEL = 0.1;
const DRONE_LEVEL = 0.5;

/**
 * Lookahead-scheduled, stem-layered music playback. One "voice" is a full
 * theme playing through four layer gains (bass/melody/pad/perc) into its
 * own master gain; intensity changes ramp the layer mix (see
 * musicThemes.js's `intensity` table), theme changes crossfade an entire
 * new voice in while the old one ramps out and self-destructs — never a
 * hard cut, never a JS timer racing the audio clock.
 *
 * Lifecycle is an explicit tiny state machine ('stopped' | 'playing'):
 * play() while playing the same theme only retunes intensity; play()
 * during/after a stop builds a fresh voice and crossfades — so rapid
 * menu <-> run flickers degrade to a clean crossfade instead of leaked
 * timers or double-scheduled loops. The driver interval exists only while
 * state is 'playing'.
 *
 * Constructed with a context *getter*, not a context — AudioSystem creates
 * the AudioContext lazily on first user gesture (autoplay policy), and
 * this must not front-run that.
 */
export class MusicDirector {
  constructor(getContext, themes) {
    this._getContext = getContext;
    this._themes = themes;
    this.state = 'stopped';
    this._themeId = null;
    this._intensity = 0;
    this._volume = 0.06;
    this._driver = null;
    this._voice = null;
    this._noiseBuffer = null;
  }

  /** Master music level (already includes the user's volume setting — see AudioSystem). */
  setVolume(v) {
    this._volume = v;
    if (this._voice) {
      const now = this._voice.ctx.currentTime;
      this._voice.master.gain.cancelScheduledValues(now);
      this._voice.master.gain.setTargetAtTime(v, now, 0.05);
    }
  }

  play(themeId, intensity = this._intensity) {
    const theme = this._themes[themeId];
    if (!theme) return;
    if (this.state === 'playing' && this._themeId === themeId) {
      this.setIntensity(intensity);
      return;
    }
    const ctx = this._getContext();
    if (this._voice) this._retire(this._voice); // crossfade: old voice ramps out while the new one ramps in
    this._themeId = themeId;
    this._intensity = intensity;
    this._voice = this._buildVoice(ctx, themeId);
    this.state = 'playing';
    this._applyIntensity(true);
    if (!this._driver) this._driver = setInterval(() => this._tick(), DRIVER_INTERVAL_MS);
    this._tick(); // schedule the first window immediately rather than waiting one driver interval
  }

  /** Ramps the layer mix to the theme's `intensity[level]` row (clamped). */
  setIntensity(level) {
    this._intensity = level;
    if (this.state === 'playing') this._applyIntensity();
  }

  stop() {
    if (this.state !== 'playing') return;
    if (this._voice) this._retire(this._voice);
    this._voice = null;
    if (this._driver) {
      clearInterval(this._driver);
      this._driver = null;
    }
    this.state = 'stopped';
  }

  _applyIntensity(immediate = false) {
    const voice = this._voice;
    const theme = this._themes[this._themeId];
    if (!voice || !theme) return;
    const rows = theme.intensity;
    const mix = rows[Math.max(0, Math.min(rows.length - 1, Math.round(this._intensity)))];
    const now = voice.ctx.currentTime;
    for (const layer of ['bass', 'melody', 'pad', 'perc']) {
      const gain = voice.layers[layer].gain;
      const target = mix[layer] ?? 0;
      if (immediate) gain.setValueAtTime(target, now);
      else gain.setTargetAtTime(target, now, INTENSITY_RAMP_SEC / 3);
    }
  }

  _buildVoice(ctx, themeId) {
    const theme = this._themes[themeId];
    const master = ctx.createGain();
    // Fade in from silence — the incoming half of every theme crossfade.
    master.gain.setValueAtTime(0.0001, ctx.currentTime);
    master.gain.linearRampToValueAtTime(this._volume, ctx.currentTime + CROSSFADE_SEC);
    master.connect(ctx.destination);

    const layers = {};
    for (const name of ['bass', 'melody', 'pad', 'perc']) {
      const g = ctx.createGain();
      g.gain.value = 0;
      g.connect(master);
      layers[name] = g;
    }

    // Constant root-note drone under everything — the one carry-over from
    // the old music system, now tuned per theme.
    const droneGain = ctx.createGain();
    droneGain.gain.value = DRONE_LEVEL;
    droneGain.connect(master);
    const drone = ctx.createOscillator();
    drone.type = 'sine';
    drone.frequency.value = theme.droneFreq;
    drone.connect(droneGain);
    drone.start();

    return { ctx, themeId, master, layers, drone, nextNoteTime: ctx.currentTime + 0.05, step: 0 };
  }

  /** Ramp a voice out and let its nodes self-destruct — no bookkeeping survives it. */
  _retire(voice) {
    const now = voice.ctx.currentTime;
    voice.master.gain.cancelScheduledValues(now);
    voice.master.gain.setTargetAtTime(0.0001, now, CROSSFADE_SEC / 4);
    // Fire-and-forget note oscillators already carry their own stop()
    // timestamps; only the persistent drone needs an explicit end.
    voice.drone.stop(now + CROSSFADE_SEC + 0.1);
  }

  _tick() {
    const voice = this._voice;
    if (this.state !== 'playing' || !voice) return;
    const theme = this._themes[voice.themeId];
    const stepDur = 60 / theme.bpm / 2; // 8th notes
    while (voice.nextNoteTime < voice.ctx.currentTime + LOOKAHEAD_SEC) {
      this._scheduleStep(voice, theme, voice.step, voice.nextNoteTime, stepDur);
      voice.nextNoteTime += stepDur;
      voice.step++;
    }
  }

  _scheduleStep(voice, theme, step, when, stepDur) {
    const i = step % STEPS;
    if (theme.bass[i]) this._note(voice, 'bass', theme.bassWave, theme.bass[i], when, stepDur * 0.95, NOTE_LEVEL.bass);
    if (theme.melody[i]) this._note(voice, 'melody', theme.melodyWave, theme.melody[i], when, stepDur * 1.8, NOTE_LEVEL.melody);
    if (theme.kick[i]) this._kick(voice, when);
    if (theme.hat[i]) this._hat(voice, when);
    // One sustained chord per bar (8 steps), cycling through theme.pads.
    if (i % 8 === 0 && theme.pads?.length) {
      const chord = theme.pads[Math.floor(step / 8) % theme.pads.length];
      for (const freq of chord) this._pad(voice, freq, when, stepDur * 8);
    }
  }

  _note(voice, layer, wave, freq, when, dur, level) {
    const ctx = voice.ctx;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = wave;
    osc.frequency.setValueAtTime(freq, when);
    gain.gain.setValueAtTime(0.0001, when);
    gain.gain.exponentialRampToValueAtTime(level, when + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, when + dur);
    osc.connect(gain).connect(voice.layers[layer]);
    osc.start(when);
    osc.stop(when + dur + 0.05);
  }

  _kick(voice, when) {
    const ctx = voice.ctx;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(150, when);
    osc.frequency.exponentialRampToValueAtTime(45, when + 0.12);
    gain.gain.setValueAtTime(KICK_LEVEL, when);
    gain.gain.exponentialRampToValueAtTime(0.0001, when + 0.15);
    osc.connect(gain).connect(voice.layers.perc);
    osc.start(when);
    osc.stop(when + 0.16);
  }

  _hat(voice, when) {
    const ctx = voice.ctx;
    if (!this._noiseBuffer) {
      const len = Math.floor(ctx.sampleRate * 0.1);
      this._noiseBuffer = ctx.createBuffer(1, len, ctx.sampleRate);
      const data = this._noiseBuffer.getChannelData(0);
      for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
    }
    const src = ctx.createBufferSource();
    src.buffer = this._noiseBuffer;
    const filter = ctx.createBiquadFilter();
    filter.type = 'highpass';
    filter.frequency.value = 6000;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(HAT_LEVEL, when);
    gain.gain.exponentialRampToValueAtTime(0.0001, when + 0.05);
    src.connect(filter).connect(gain).connect(voice.layers.perc);
    src.start(when);
    src.stop(when + 0.06);
  }

  _pad(voice, freq, when, dur) {
    const ctx = voice.ctx;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(freq, when);
    // Slow swell in and out — pads are texture, not attack.
    gain.gain.setValueAtTime(0.0001, when);
    gain.gain.linearRampToValueAtTime(NOTE_LEVEL.pad, when + dur * 0.3);
    gain.gain.setValueAtTime(NOTE_LEVEL.pad, when + dur * 0.7);
    gain.gain.linearRampToValueAtTime(0.0001, when + dur);
    osc.connect(gain).connect(voice.layers.pad);
    osc.start(when);
    osc.stop(when + dur + 0.05);
  }
}
